import {
  DEFAULT_REPORT_PERIOD,
  getSharmaReportsBaseUrl,
} from "../config/sharmaReportsConfig";
import {
  enrichSharmaPayload,
  formatSharmaChartFailureFromError,
  formatSharmaChartFailure,
  formatSharmaTableFailureFromError,
  formatSharmaTableFailure,
  formatSharmaPartialSuccess,
  resolveActiveLocale,
  REASON_CODES,
  errorToReasonCode,
} from "./sharmaReportMessages";

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * GET JSON with retries (cold start / transient failures).
 * Query string uses `portfolio_id` and `perd`.
 *
 * @param {string} path - begins with /
 * @param {{ portfolioApiId: string, period?: string, perd?: string, extraQuery?: Record<string, string>, signal?: AbortSignal, retries?: number }} opts
 */
export async function fetchSharmaReportJson(path, opts) {
  const {
    portfolioApiId,
    period: periodOpt,
    perd: perdOpt,
    extraQuery,
    signal,
    retries = 3,
  } = opts || {};
  const period = periodOpt ?? perdOpt ?? DEFAULT_REPORT_PERIOD;
  const base = getSharmaReportsBaseUrl();
  const params = new URLSearchParams();
  params.set("portfolio_id", portfolioApiId);
  params.set("perd", period);
  if (extraQuery && typeof extraQuery === "object") {
    for (const [key, value] of Object.entries(extraQuery)) {
      if (value != null && value !== "") {
        params.set(key, String(value));
      }
    }
  }
  const url = `${base}${path}?${params.toString()}`;

  let lastErr;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url, { method: "GET", signal });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    } catch (e) {
      lastErr = e;
      if (signal?.aborted) {
        throw e;
      }
      const backoff = Math.min(1000 * 2 ** attempt, 8000);
      await sleep(backoff);
    }
  }
  throw lastErr ?? new Error("request_failed");
}

/** @deprecated Prefer {@link formatSharmaTableFailure} / {@link formatSharmaChartFailure} */
export function formatSharmaReportError(err) {
  const locale = resolveActiveLocale();
  const msg = err?.message || String(err || "");
  if (locale === "es") {
    if (msg.includes("HTTP")) {
      return `El servicio de reportes respondió con error (${msg}). Intenta más tarde.`;
    }
    if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
      return "No hay conexión con el servidor de reportes. Comprueba tu red e intenta de nuevo.";
    }
    return `No se pudo mostrar el reporte: ${msg}`;
  }
  if (msg.includes("HTTP")) {
    return `The report service returned an error (${msg}). Please try again later.`;
  }
  if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
    return "Could not reach the report server. Check your network and try again.";
  }
  return `Could not display the report: ${msg}`;
}

export function formatSharmaTableLoadError(err) {
  return formatSharmaTableFailureFromError(err);
}

export function formatSharmaChartLoadError(err) {
  return formatSharmaChartFailureFromError(err);
}

export function formatSharmaChartIntegrationError() {
  return formatSharmaChartFailure(
    resolveActiveLocale(),
    REASON_CODES.INTEGRATION_ERROR
  );
}

export {
  enrichSharmaPayload,
  formatSharmaChartFailure,
  formatSharmaTableFailure,
  formatSharmaPartialSuccess,
  resolveActiveLocale,
  REASON_CODES,
  errorToReasonCode,
};
